import { ResponsiveLine } from '@nivo/line';

import { DailyStatDataProps } from 'src/types/client/stats';

interface DailyAnnotationProgressProps {
  data: DailyStatDataProps[];
}

export default function StatsDailyProgress({
  data,
}: DailyAnnotationProgressProps): JSX.Element {
  // Transform data for line chart
  const dailyAnnotations = {
    id: 'Daily Annotations',
    data: data.map(d => ({ x: d.date, y: d.count })),
  };

  // Calculate cumulative data
  let cumulative = 0;
  const cumulativeData = {
    id: 'Cumulative Total',
    data: data.map(d => {
      cumulative += d.count;
      return { x: d.date, y: cumulative };
    }),
  };

  const chartData = [dailyAnnotations, cumulativeData];

  return (
    <ResponsiveLine
      data={chartData}
      margin={{ top: 20, right: 120, bottom: 50, left: 60 }}
      xScale={{ type: 'point' }}
      yScale={{
        type: 'linear',
        min: 'auto',
        max: 'auto',
        stacked: false,
        reverse: false,
      }}
      curve="monotoneX"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: -45,
        legend: 'Date',
        legendOffset: 40,
        legendPosition: 'middle',
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: 'Count',
        legendOffset: -50,
        legendPosition: 'middle',
      }}
      colors={['#10b981', '#60a5fa']}
      pointSize={8}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={2}
      pointBorderColor={{ from: 'serieColor' }}
      pointLabelYOffset={-12}
      useMesh={true}
      enableSlices="x"
      sliceTooltip={({ slice }) => {
        return (
          <div
            style={{
              background: '#ffffff',
              padding: '12px 16px',
              borderRadius: 6,
              border: '1px solid #e5e7eb',
              color: '#0a0a0a',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              {slice.points[0]?.data.xFormatted}
            </div>
            {slice.points.map(point => (
              <div
                key={point.id}
                style={{
                  color: point.serieColor,
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: point.serieColor,
                  }}
                />
                <strong>{point.serieId}:</strong>
                <span>{point.data.yFormatted}</span>
              </div>
            ))}
          </div>
        );
      }}
      legends={[
        {
          anchor: 'bottom-right',
          direction: 'column',
          justify: false,
          translateX: 100,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 80,
          itemHeight: 20,
          itemOpacity: 0.75,
          symbolSize: 12,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
      theme={{
        axis: {
          ticks: {
            text: {
              fill: '#6a7282',
              fontSize: 11,
            },
          },
          legend: {
            text: {
              fill: '#0a0a0a',
              fontSize: 12,
              fontWeight: 600,
            },
          },
        },
        grid: {
          line: {
            stroke: '#e5e7eb',
            strokeWidth: 1,
          },
        },
        legends: {
          text: {
            fill: '#6a7282',
            fontSize: 12,
          },
        },
      }}
    />
  );
}
